import React from "react";

// CONFIGURACIÓN COMPARTIDA DE LOS GRÁFICOS
//
// Dos cosas que faltaban en los 17 gráficos de Estadísticas:
//
// 1. NO SE PODÍA SACAR NADA. La barra de herramientas de ApexCharts estaba
//    apagada en todos (`toolbar: { show: false }`), así que el dueño no tenía
//    forma de llevarse un gráfico a un mail, a un WhatsApp o a una reunión con
//    el contador. La capacidad ya estaba instalada y pagada: sólo estaba
//    apagada.
//
// 2. ESTABA EN INGLÉS. Sin locale, ApexCharts escribe los meses, los días y el
//    menú de descarga en inglés — en un producto que usan en 12 países de
//    habla hispana.
//
// El zoom y el paneo quedan APAGADOS a propósito: en un gráfico de 14 barras
// no sirven, y en el teléfono se disparan solos con el gesto de scroll.

const ES = {
  name: "es",
  options: {
    months: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
             "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
    shortMonths: ["Ene", "Feb", "Mar", "Abr", "May", "Jun",
                  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    days: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
    shortDays: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
    toolbar: {
      exportToSVG: "Descargar imagen (SVG)",
      exportToPNG: "Descargar imagen (PNG)",
      exportToCSV: "Descargar datos (Excel)",
      menu: "Descargar",
      selection: "Seleccionar",
      selectionZoom: "Acercar selección",
      zoomIn: "Acercar",
      zoomOut: "Alejar",
      pan: "Desplazar",
      reset: "Restablecer",
    },
  },
};

/**
 * Se esparce dentro de `chart: {}` y reemplaza al viejo `toolbar: {show:false}`.
 * Va con `...` para que cada gráfico conserve su type, height y demás.
 */
export const TOOLBAR_DESCARGA = {
  locales: [ES],
  defaultLocale: "es",
  toolbar: {
    show: true,
    offsetY: -4,
    tools: {
      download: true,
      // Todo lo demás apagado: ver la nota de arriba.
      selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false,
    },
    export: {
      // `;` y no `,`: es lo que espera el Excel configurado en español, que es
      // el que tienen los clientes. Con coma, todo cae en una sola columna.
      csv: { filename: "estadisticas", columnDelimiter: ";", headerCategory: "Periodo" },
      svg: { filename: "estadisticas" },
      png: { filename: "estadisticas" },
    },
  },
};


// ── LA LEYENDA ────────────────────────────────────────────────────────────
//
// Reclamo del dueño, repetido: "no sabes qué cosas son hasta que pones el mouse
// arriba". Y no era que faltara configurarla: la mitad de los gráficos ya pedía
// `legend: { position: "top" }`.
//
// La causa real estaba en el tema, no acá:
//
//     src/assets/scss/custom/plugins/_apexcharts.scss
//     .apexcharts-legend { display: none !important; }
//
// Una regla global de Upzet, con `!important`, que apagaba la leyenda de todos
// los gráficos de la aplicación. Ninguna opción de ApexCharts puede ganarle a
// eso, así que los `position: "top"` se escribían y no se veían nunca. Se quitó
// del SCSS; a partir de ahí manda lo que diga cada gráfico.
//
// Otras dos causas, más chicas, que hacían inútil la leyenda incluso encendida:
//
// · `distributed: true` reparte un color por barra, y entonces ApexCharts arma
//   la leyenda con las CATEGORÍAS, que ya están escritas en el eje. Ahí la
//   leyenda de ApexCharts no sirve: si el color significa algo —"tu mejor
//   día"— hay que escribirlo aparte, con <Referencia>.
//
// · Un `colors` más corto que la lista de series hace que ApexCharts recicle
//   desde el principio, y la leyenda queda con dos cuadraditos del mismo color
//   y nombres distintos: encendida y sin servir para nada. Pasaba en los
//   apilados, que filtran las series vacías y por eso nunca tienen la misma
//   cantidad de series dos veces. La salida no es contar bien los colores sino
//   sacarlos de la propia serie —`colors: series.map((s) => s.color)`— para que
//   no exista una lista paralela que se pueda desincronizar.
//
// La regla, una sola: **más de una serie o más de un color con significado, hay
// leyenda.** Una sola serie, no: ahí el título ya dice qué es y el cuadradito
// es ruido.

/**
 * Leyenda de series, arriba y a la izquierda: se lee antes que el gráfico,
 * que es el orden en que hace falta.
 *
 * `showForSingleSeries: false` es el default de ApexCharts y se deja explícito:
 * un gráfico de una sola serie que use esta constante no muestra nada, así que
 * se puede aplicar sin pensar.
 *
 * Los tamaños de `markers` van en width/height/radius porque el proyecto está
 * en ApexCharts 3.30: el `markers: { size, shape }` de la versión 4 se ignora
 * en silencio.
 */
export const LEYENDA = {
  show: true,
  showForSingleSeries: false,
  position: "top",
  horizontalAlign: "left",
  fontSize: "12px",
  fontWeight: 500,
  offsetY: 0,
  markers: { width: 10, height: 10, radius: 10 },
  itemMargin: { horizontal: 10, vertical: 3 },
};

/**
 * Para las donas va abajo: arriba pelea con el total que se dibuja en el
 * centro, y una dona sin leyenda es ilegible porque el nombre de cada porción
 * no está en ningún eje.
 *
 * `showForSingleSeries` no aplica: en una dona cada porción es una etiqueta, no
 * una serie, y siempre se muestran todas.
 */
export const LEYENDA_DONA = {
  ...LEYENDA,
  position: "bottom",
  horizontalAlign: "center",
  fontSize: "11.5px",
  itemMargin: { horizontal: 8, vertical: 2 },
};

/**
 * Se usa cuando la leyenda sería ruido, y siempre con un comentario al lado
 * diciendo por qué. Existe para que "sin leyenda" sea una decisión visible en
 * el código y no un olvido.
 */
export const SIN_LEYENDA = { show: false };

/**
 * Referencia de colores escrita a mano, para los gráficos donde la leyenda de
 * ApexCharts no puede explicar nada: los `distributed: true`, donde el color no
 * es una serie sino una marca —"este es tu mejor día"—.
 *
 * Va debajo del gráfico porque es la aclaración de lo que se acaba de ver.
 *
 * @param {{color: string, texto: string}[]} items
 */
export const Referencia = ({ items }) => (
  <div
    className="d-flex flex-wrap justify-content-center"
    style={{ gap: "6px 16px", fontSize: 11.5, color: "#6b7280", marginTop: 6 }}
  >
    {items.filter(Boolean).map((i) => (
      <span key={i.texto} className="d-inline-flex align-items-center" style={{ gap: 5 }}>
        <span
          aria-hidden="true"
          style={{
            width: 10, height: 10, borderRadius: 10,
            background: i.color, flexShrink: 0, display: "inline-block",
          }}
        />
        {i.texto}
      </span>
    ))}
  </div>
);
